/**
 * Dialog para editar el perfil fiscal SET de un RUC.
 *
 * Estos datos vienen de Marangatu (cuenta del contribuyente) y los
 * carga manualmente el contador. Incluyen actividad económica, domicilio
 * fiscal, día de vencimiento real (que SET asigna según terminación del
 * RUC), email/teléfono fiscales, datos del representante legal, etc.
 *
 * El RUC y la razón social NO son editables acá — son inmutables vía
 * SET validation. Si necesitan cambio, eliminar y recrear el RUC.
 *
 * En el futuro, cuando integremos SIFEN oficial, estos campos se
 * sincronizarán automáticamente y este dialog quedará como fallback
 * para edición manual.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import { api, ApiCallError } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PhoneInput, {
  splitE164ForDisplay,
  toInternationalPhoneDigits,
  isValidPhone,
} from '@/components/ui/phone-input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';

interface FiscalProfileData {
  nombreFantasia?: string | null;
  actividadEconomica?: string | null;
  codigoActividad?: string | null;
  domicilioFiscal?: string | null;
  fechaInicioActividades?: string | null;
  emailFiscal?: string | null;
  telefonoFiscal?: string | null;
  diaVencimientoIva?: number | null;
  diaVencimientoIrp?: number | null;
  representanteLegalRuc?: string | null;
  representanteLegalNombre?: string | null;
}

interface Props {
  tenantId: string;
  rucId: string;
  ruc: string;
  razonSocial: string;
  regimen: 'iva_irp' | 'ire_simple';
  initial: FiscalProfileData;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FiscalProfileDialog({
  tenantId,
  rucId,
  ruc,
  razonSocial,
  regimen,
  initial,
  open,
  onOpenChange,
}: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState<FiscalProfileData>(initial);
  // Teléfono fiscal es opcional: válido si está vacío o si libphonenumber lo acepta.
  const initialPhone = splitE164ForDisplay(initial.telefonoFiscal);
  const [phoneCountry, setPhoneCountry] = useState(initialPhone.countryCode);
  const [phoneNational, setPhoneNational] = useState(initialPhone.nationalNumber);
  const telValid = phoneNational.trim() === '' || isValidPhone(phoneCountry, phoneNational);

  const handlePhone = (country: string, national: string) => {
    setPhoneCountry(country);
    setPhoneNational(national);
    setForm((f) => ({
      ...f,
      telefonoFiscal: national.trim() ? toInternationalPhoneDigits(country, national) : '',
    }));
  };

  const mutation = useMutation({
    mutationFn: () => {
      // Convertir strings vacíos a null para que el server pueda
      // limpiar campos. Day fields son números, parseamos al toque.
      const clean: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(form)) {
        if (typeof v === 'string' && v.trim() === '') {
          clean[k] = null;
        } else {
          clean[k] = v;
        }
      }
      return api(`/api/admin/tenants/${tenantId}/rucs/${rucId}`, {
        method: 'PATCH',
        body: JSON.stringify(clean),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
      toast.success('Perfil fiscal actualizado');
      onOpenChange(false);
    },
    onError: (err) => {
      if (err instanceof ApiCallError && err.body.issues) {
        const firstField = Object.keys(err.body.issues)[0] ?? 'el formulario';
        toast.error(`Campo inválido: ${firstField}`);
      } else {
        toast.error('No se pudo guardar el perfil');
      }
    },
  });

  const isJuridica = regimen !== 'iva_irp';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-4xl max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 />
            Perfil fiscal SET
          </DialogTitle>
          <DialogDescription>
            Datos que se obtienen entrando a la cuenta del contribuyente en
            Marangatu. Completá lo que sepas — el sistema los usa para
            cálculos de vencimiento real, generación de Hechauka y
            notificaciones precisas.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md bg-muted p-3 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">RUC</span>
              <span className="font-mono">{ruc}</span>
            </div>
            <div className="flex justify-between gap-4 mt-1">
              <span className="text-muted-foreground">Razón social</span>
              <span className="text-right">{razonSocial}</span>
            </div>
          </div>

          <Section title="Datos comerciales">
            <Field
              label="Nombre fantasía"
              hint="Nombre comercial (si difiere de razón social)"
              value={form.nombreFantasia ?? ''}
              onChange={(v) => setForm({ ...form, nombreFantasia: v })}
            />
            <Field
              label="Actividad económica"
              hint="Descripción registrada en SET"
              value={form.actividadEconomica ?? ''}
              onChange={(v) => setForm({ ...form, actividadEconomica: v })}
            />
            <Field
              label="Código de actividad"
              hint="CGCE/CIIU numérico"
              value={form.codigoActividad ?? ''}
              onChange={(v) => setForm({ ...form, codigoActividad: v })}
            />
            <Field
              label="Domicilio fiscal"
              value={form.domicilioFiscal ?? ''}
              onChange={(v) => setForm({ ...form, domicilioFiscal: v })}
            />
            <Field
              label="Fecha inicio actividades"
              type="date"
              value={form.fechaInicioActividades ?? ''}
              onChange={(v) => setForm({ ...form, fechaInicioActividades: v })}
            />
          </Section>

          <Section title="Contacto registrado SET">
            <Field
              label="Email fiscal"
              hint="Email registrado en Marangatu (puede diferir del de WhatsApp)"
              type="email"
              value={form.emailFiscal ?? ''}
              onChange={(v) => setForm({ ...form, emailFiscal: v })}
            />
            <div className="space-y-1">
              <Label className="text-xs">Teléfono fiscal</Label>
              <PhoneInput
                showLabel={false}
                countryCode={phoneCountry}
                phoneNumber={phoneNational}
                onCountryCodeChange={(c) => handlePhone(c, phoneNational)}
                onPhoneNumberChange={(n) => handlePhone(phoneCountry, n)}
                placeholder="0991 123-456"
              />
              {!telValid && (
                <p className="text-[10px] text-destructive">Número de teléfono inválido.</p>
              )}
            </div>
          </Section>

          <Section title="Calendario fiscal">
            <Field
              label="Día vencimiento IVA"
              hint="Día del mes siguiente al período, según terminación de RUC (1-31)"
              type="number"
              min={1}
              max={31}
              value={form.diaVencimientoIva?.toString() ?? ''}
              onChange={(v) =>
                setForm({ ...form, diaVencimientoIva: v ? Number(v) : null })
              }
            />
            <Field
              label="Día vencimiento IRP"
              hint="Análogo para declaración anual de IRP (1-31)"
              type="number"
              min={1}
              max={31}
              value={form.diaVencimientoIrp?.toString() ?? ''}
              onChange={(v) =>
                setForm({ ...form, diaVencimientoIrp: v ? Number(v) : null })
              }
            />
          </Section>

          {isJuridica && (
            <Section title="Representante legal (solo personas jurídicas)">
              <Field
                label="RUC del representante"
                value={form.representanteLegalRuc ?? ''}
                onChange={(v) => setForm({ ...form, representanteLegalRuc: v })}
              />
              <Field
                label="Nombre del representante"
                value={form.representanteLegalNombre ?? ''}
                onChange={(v) =>
                  setForm({ ...form, representanteLegalNombre: v })
                }
              />
            </Section>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || !telValid}>
            {mutation.isPending ? 'Guardando…' : 'Guardar perfil'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-foreground border-b border-border pb-1">
        {title}
      </h3>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function Field({
  label,
  hint,
  value,
  onChange,
  type = 'text',
  min,
  max,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'date' | 'number' | 'email';
  min?: number;
  max?: number;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        min={min}
        max={max}
      />
      {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
    </div>
  );
}
